// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AppEvent } from '@interfaces/interfaces';
import { FormControl, FormGroup } from '@angular/forms';
import { ApiService } from '@services/api.service';
import { ErrorService } from '@services/error.service';
import { SettingsService } from '@services/settings.service';

type VerificationResponse = {
    tokenId: bigint;
    oemCode: string;
    oemDealerId: string;
    dealerName: string;
    dealerId: string;
};

@Component({
    selector: 'app-token-verification',
    templateUrl: './token-verification.component.html',
    styleUrls: ['./token-verification.component.scss'],
})
export class TokenVerificationComponent implements OnInit {
    @Output() signal: EventEmitter<AppEvent> = new EventEmitter();
    @Input() token: string;
    @Input() oemCode: string;
    @Input() jacketId?: string;
    attemptsRemaining = 3;
    oemDealerId = '';
    tenantSupportEmail = '';
    tenantSupportPhone = '';

    form = new FormGroup({
        oemDealerId: new FormControl(''),
    });

    constructor(
        private readonly apiService: ApiService,
        private readonly errorService: ErrorService,
        private readonly settingsService: SettingsService
    ) {}

    async ngOnInit(): Promise<void> {
        const updateOemSettings = async (oemCode) => {
            const { supportEmail, supportPhone } = await this.settingsService.getOemSettings(oemCode);
            this.tenantSupportEmail = supportEmail;
            this.tenantSupportPhone = supportPhone;
        };

        await updateOemSettings(this.oemCode);
        this.settingsService.oemCodeEvent().subscribe(updateOemSettings);
    }

    oemDealerCodeChanged() {
        this.oemDealerId = this.form.controls.oemDealerId.value.trim();
    }

    submit() {
        if (!this.oemDealerId) {
            return;
        }

        // here, we want to pass the destinationUri [/dashboard/document/{id}], so the api can validate that instead of the token, if present

        this.apiService
            .postUnauthenticatedV2<VerificationResponse>('api/auth/enrollment/verify', {
                oemDealerId: this.oemDealerId,
                token: this.token,
                jacketId: this.jacketId,
            })
            .subscribe((resp) => {
                if (resp) {
                    const data = resp.data;

                    if (data && data.dealerId) {
                        this.signal.emit({
                            context: 'data',
                            data: {
                                dealerName: data.dealerName,
                                oemDealerId: data.oemDealerId,
                                dealerId: data.dealerId,
                            },
                        });
                        this.signal.emit({ context: 'next', data: null });
                    }
                } else {
                    // wrong oem dealer id
                    this.attemptsRemaining--;

                    if (0 < this.attemptsRemaining) {
                        this.errorService.showError({
                            statusCode: 500,
                            title: 'Incorrect Dealer Code',
                            message: `The dealer code you entered is either invalid or does not correspond with the link you used to access EZ DOCS.  Please close this window and try again.
                        
If you have any questions or need help, please reach out to us.

EZ DOCS Support
Phone: ${this.tenantSupportPhone}
Email: ${this.tenantSupportEmail}`,
                        });
                    } else {
                        this.errorService.showError({
                            statusCode: 500,
                            title: 'Maximum Number of Attempts Reached',
                            message: `You have made three unsuccessful attempts to access EZ DOCS using a dealer code that is either invalid or does not correspond with the link you used to access the application.  Please reach out for assistance accessing EZ DOCS.

EZ DOCS Support
Phone: ${this.tenantSupportPhone}
Email: ${this.tenantSupportEmail}`,
                        });
                    }
                }
            });
    }

    cancel() {
        this.signal.emit({ context: 'cancel', data: null });
    }
}
